/**
 * Adds a class to the Swiper container the first time the user interacts with it.
 * All listeners are cleaned up after first interaction or when the swiper is destroyed.
 *
 * @param {import('swiper').Swiper} swiper
 * @param {object} [opts]
 * @param {string} [opts.className='has-interacted']
 * @returns {() => void} cleanup function
 */
export function markInteractedWith(swiper, opts = {}) {
  const className = opts.className ?? 'has-interacted';

  if (!swiper || !swiper.el) {
    throw new Error('attachInteractedClass: invalid Swiper instance');
  }

  const container = swiper.el;

  // If already marked, do nothing
  if (container.classList.contains(className)) {
    return () => {};
  }

  let done = false;

  const toArray = (el) => {
    if (!el) return [];
    if (Array.isArray(el)) return el.filter(Boolean);
    if (
      typeof NodeList !== 'undefined' &&
      (el instanceof NodeList || el instanceof HTMLCollection)
    ) {
      return Array.from(el);
    }
    return [el];
  };

  const domListeners = [];
  const swiperListeners = [];

  function addDom(el, type, handler, options) {
    toArray(el).forEach((node) => {
      node.addEventListener(type, handler, options);
      domListeners.push({ node, type, handler, options });
    });
  }

  function onSwiper(type, handler) {
    swiper.on(type, handler);
    swiperListeners.push({ type, handler });
  }

  function cleanup() {
    // Remove Swiper event handlers
    while (swiperListeners.length) {
      const { type, handler } = swiperListeners.pop();
      try {
        swiper.off(type, handler);
      } catch {}
    }
    // Remove DOM listeners
    while (domListeners.length) {
      const { node, type, handler, options } = domListeners.pop();
      try {
        node.removeEventListener(type, handler, options);
      } catch {
        try {
          node.removeEventListener(type, handler);
        } catch {}
      }
    }
  }

  function handleInteraction() {
    if (done) return;
    done = true;
    container.classList.add(className);
    cleanup();
  }

  // Swiper events reflecting user input
  onSwiper('touchStart', handleInteraction); // touch or mouse drag
  onSwiper('click', handleInteraction);

  // Container DOM interactions
  ['pointerdown', 'wheel', 'keydown'].forEach((type) =>
    addDom(container, type, handleInteraction, { passive: true }),
  );

  // Navigation / pagination clicks also count as interaction
  if (swiper.navigation) {
    addDom(swiper.navigation.nextEl, 'click', handleInteraction, {
      passive: true,
    });
    addDom(swiper.navigation.prevEl, 'click', handleInteraction, {
      passive: true,
    });
  }
  if (swiper.pagination && swiper.pagination.el) {
    addDom(swiper.pagination.el, 'click', handleInteraction, { passive: true });
  }

  // Auto-cleanup on swiper destroy to prevent leaks
  onSwiper('destroy', cleanup);

  return cleanup;
}
